import { useEffect, useState, useMemo, useCallback } from "react";
import { Clock, Filter, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Avatar } from "@/components/BrandMark";
import { relativeTime } from "@/lib/time";
import { describeActivity, actionCategory, ACTIVITY_CATEGORIES } from "@/lib/activity";

const PAGE_SIZE = 50;

function CategoryPill({ category }) {
    const styles = {
        templates: { bg: "#E7F0FA", fg: "#1A3E6D" },
        users_auth: { bg: "#FBE9E7", fg: "#8B2A1F" },
        brands_categories: { bg: "#F0F7E0", fg: "#4D7B1B" },
        other: { bg: "#EEF0F3", fg: "#6B7682" },
    };
    const labels = {
        templates: "Template",
        users_auth: "Users & auth",
        brands_categories: "Brand",
        other: "Activity",
    };
    const s = styles[category] || styles.other;
    return (
        <span
            className="tms-chip"
            style={{ background: s.bg, color: s.fg, height: 20, padding: "0 8px", fontSize: 10, fontWeight: 600, letterSpacing: "0.04em" }}
        >
            {labels[category] || category}
        </span>
    );
}

export default function ActivityPage() {
    const { users } = useData();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState("all");
    const [actorId, setActorId] = useState("");
    const [offset, setOffset] = useState(0);
    const [error, setError] = useState("");

    const activeCategory = useMemo(
        () => ACTIVITY_CATEGORIES.find((c) => c.key === category) || ACTIVITY_CATEGORIES[0],
        [category]
    );

    const fetchPage = useCallback(
        async (nextOffset, replace) => {
            setLoading(true);
            setError("");
            try {
                const params = new URLSearchParams();
                params.set("limit", String(PAGE_SIZE));
                params.set("offset", String(nextOffset));
                for (const p of activeCategory.prefixes) params.append("action_prefix", p);
                if (actorId) params.set("actor_id", actorId);
                const { data } = await api.get(`/activity?${params.toString()}`);
                setTotal(data.total);
                setItems((prev) => (replace ? data.items : [...prev, ...data.items]));
                setOffset(nextOffset + data.items.length);
            } catch (e) {
                setError(e.response?.data?.detail || e.message);
            }
            setLoading(false);
        },
        [activeCategory, actorId]
    );

    useEffect(() => {
        // Reset when filters change.
        fetchPage(0, true);
    }, [fetchPage]);

    const canLoadMore = offset < total;

    return (
        <div className="max-w-[1080px] mx-auto px-6 py-6 space-y-6" data-testid="activity-page">
            <div className="flex items-center gap-3 mb-2">
                <Clock size={20} style={{ color: "var(--tms-navy)" }} />
                <h1 style={{ fontSize: 26, fontWeight: 600, color: "var(--tms-navy)" }}>Activity log</h1>
                <span className="tms-chip-count" data-testid="activity-total">{total}</span>
            </div>
            <p style={{ color: "var(--tms-muted)", fontSize: 13, marginTop: -8, marginBottom: 12 }}>
                A running audit trail of everything advisers and admins do in the library.
            </p>

            {/* Filters */}
            <div className="tms-card p-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                    <Filter size={14} style={{ color: "var(--tms-muted)" }} />
                    <span style={{ fontSize: 12, color: "var(--tms-muted)", fontWeight: 500 }}>Filters</span>
                </div>
                <select
                    className="tms-select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid="activity-filter-category"
                    style={{ minWidth: 180 }}
                >
                    {ACTIVITY_CATEGORIES.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                </select>
                <select
                    className="tms-select"
                    value={actorId}
                    onChange={(e) => setActorId(e.target.value)}
                    data-testid="activity-filter-actor"
                    style={{ minWidth: 200 }}
                >
                    <option value="">All actors</option>
                    {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                </select>
            </div>

            {/* List */}
            {error && (
                <div
                    className="tms-card p-4"
                    style={{ color: "var(--tms-danger)", fontSize: 13 }}
                    data-testid="activity-error"
                >
                    {error}
                </div>
            )}
            <div className="tms-card overflow-hidden" data-testid="activity-list">
                {items.length === 0 && !loading && (
                    <div className="p-8 text-center" data-testid="activity-empty">
                        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 4 }}>
                            No activity yet for these filters
                        </div>
                        <div style={{ color: "var(--tms-muted)", fontSize: 13 }}>
                            Actions across templates, brands and team members will show up here.
                        </div>
                    </div>
                )}
                <div className="divide-y" style={{ borderColor: "var(--tms-border)" }}>
                    {items.map((it) => {
                        const cat = actionCategory(it.action);
                        const isSystem = !it.actor_id;
                        return (
                            <div
                                key={it.id}
                                className="p-4 flex items-start gap-3"
                                data-testid={`activity-entry-${it.id}`}
                            >
                                <Avatar
                                    initials={it.actor_initials || (isSystem ? "•" : "?")}
                                    name={it.actor_name || "System"}
                                    size="md"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--tms-navy)" }}>
                                            {it.actor_name || "System"}
                                        </span>
                                        <CategoryPill category={cat} />
                                    </div>
                                    <div style={{ fontSize: 13, color: "var(--tms-text)", marginTop: 4, lineHeight: 1.5 }}>
                                        {describeActivity(it)}
                                    </div>
                                    <div className="flex items-center gap-3 mt-1" style={{ fontSize: 11, color: "var(--tms-faint)" }}>
                                        <span title={it.ts}>{relativeTime(it.ts)}</span>
                                        {it.ip && <span data-testid={`activity-entry-ip-${it.id}`}>· ip {it.ip}</span>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex justify-center">
                {canLoadMore && (
                    <button
                        className="btn-outline"
                        onClick={() => fetchPage(offset, false)}
                        disabled={loading}
                        data-testid="activity-load-more-btn"
                    >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                        {loading ? "Loading…" : `Load more (${total - offset} left)`}
                    </button>
                )}
                {!canLoadMore && items.length > 0 && (
                    <span style={{ fontSize: 12, color: "var(--tms-muted)" }}>
                        End of activity log
                    </span>
                )}
            </div>
        </div>
    );
}
